import { Clock, MapPin } from '@chain-zen/ui'

import { getAssetStatus, getRelativeTime } from '../../../../../../../utils/eld-utils'

interface IPreviewAddress {
  addressLabel?: string
  city?: string
  coordinates?: { latitude?: number; longitude?: number }
  state?: string
  stateCode?: string
}

interface ILocationPreviewPanelProps {
  address?: IPreviewAddress | unknown | null
  isoDateTime: string
}

/**
 * Displays a mini map placeholder with location details and signal indicator.
 * Used inside AssetItem to show the location preview when expanded.
 */
export const LocationPreviewPanel = ({ address, isoDateTime }: ILocationPreviewPanelProps) => {
  const previewAddress = address as IPreviewAddress | null | undefined
  const assetStatus = getAssetStatus(isoDateTime)
  const hasLocation = !!address

  /**
   * Formats the address for display, prioritizing the most specific information available.
   */
  const getDisplayAddress = (): string => {
    if (previewAddress?.addressLabel) {
      return previewAddress.addressLabel
    }
    if (previewAddress?.city) {
      return `${previewAddress.city}, ${previewAddress.stateCode || previewAddress.state || ''}`.trim()
    }
    if (previewAddress?.coordinates) {
      return `${previewAddress.coordinates.latitude?.toFixed(4)}, ${previewAddress.coordinates.longitude?.toFixed(4)}`
    }
    return 'Location found'
  }

  return (
    <div className="px-3 pb-2.5 pt-0">
      <div className="bg-card rounded-lg p-2.5 border border-border">
        <div className="flex items-center gap-2.5">
          {/* Mini map placeholder */}
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
            {hasLocation ? (
              <div className="relative w-full h-full bg-gradient-to-br from-emerald-900/30 to-muted">
                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-30">
                  <div className="w-full h-px bg-muted-foreground absolute top-1/4" />
                  <div className="w-full h-px bg-muted-foreground absolute top-1/2" />
                  <div className="w-full h-px bg-muted-foreground absolute top-3/4" />
                  <div className="w-px h-full bg-muted-foreground absolute left-1/4" />
                  <div className="w-px h-full bg-muted-foreground absolute left-1/2" />
                  <div className="w-px h-full bg-muted-foreground absolute left-3/4" />
                </div>
                {/* Pulsating dot */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse shadow-lg shadow-emerald-500/50" />
                </div>
              </div>
            ) : (
              <MapPin className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
            )}
          </div>

          {/* Location details */}
          <div className="flex-1 min-w-0">
            {hasLocation ? (
              <>
                <div className="relative group/address">
                  <div className="text-foreground text-sm leading-snug truncate">{getDisplayAddress()}</div>
                  {/* Tooltip on hover for full address */}
                  <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-popover border border-border rounded text-xs text-popover-foreground whitespace-normal max-w-[250px] opacity-0 invisible group-hover/address:opacity-100 group-hover/address:visible transition-opacity z-50 pointer-events-none shadow-lg">
                    {getDisplayAddress()}
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {getRelativeTime(isoDateTime)}
                </div>
              </>
            ) : (
              <div className="text-muted-foreground text-sm">Location unavailable</div>
            )}
          </div>

          {/* Signal indicator */}
          {!!(hasLocation && assetStatus === 'active') && (
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 rounded shrink-0">
              <div className="flex gap-0.5">
                <div className="w-0.5 h-2 bg-emerald-500 rounded-sm" />
                <div className="w-0.5 h-2.5 bg-emerald-500 rounded-sm" />
                <div className="w-0.5 h-3 bg-emerald-500 rounded-sm" />
              </div>
              <span className="text-emerald-400 text-xs font-medium">Live</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
